import type { Unit } from '@/types/api';

// Natural-order comparison for unit numbers. Uses the Intl `numeric`
// option so digit runs anywhere in the string compare as numbers, not
// lexicographically. Handles every case the previous "must start with
// a digit" regex couldn't:
//
//   "1", "2", "10"               → 1 < 2 < 10                ✓
//   "2A", "2B"                   → 2A < 2B                   ✓
//   "Unit A 1", "Unit A 10"      → A 1 < A 2 < … < A 10      ✓ (the
//     case the previous prefix-regex variant got wrong — see the
//     May 15 screenshot where "A 1, A 10, A 2…" interleaved.)
//   "1", "Penthouse"             → 1 < Penthouse             ✓
//   "Penthouse", "North"         → North < Penthouse         ✓
//
// `sensitivity: 'base'` makes the comparison case-insensitive so
// "2A" and "2a" don't split the order.
export function compareUnitNumbers(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Sorts units by unit number using natural order (1 < 2 < 10), so a
 * NextCentury-style "Building A 1 … Building A 100" list renders in the
 * order a human would expect rather than as the lexicographic
 * "1, 10, 11, … 19, 2, 20, …" that plain string sort produces.
 */
export function sortUnitsForDisplay(units: Unit[]): Unit[] {
  return [...units].sort((a, b) => compareUnitNumbers(a.unitNumber, b.unitNumber));
}
